import React, { useMemo, useEffect, useRef } from 'react';
import { Table, Input, Button, InputNumber, Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { columns as readonlyColumns } from './columns';
import { updateFormField } from 'store/forms/actions';
import DatePicker from 'components/datepicker';
import { ReactComponent as Bin } from 'assets/icons/bin.svg';

const MAX_TOTAL = 100;

// Creates an empty beneficiary row
const createEmptyBeneficiary = () => ({
  id: Date.now().toString(),
  fullName: '',
  afm: '',
  dateOfBirth: '',
  participationRate: '',
  role: '',
});

const SubTab3 = () => {
  console.log('SubTab3 render');
  const dispatch = useDispatch();

  const companyLegalType = useSelector(
    state => state.forms.carrier.companyLegalType
  );
  const isAtomiki = companyLegalType === 'Ατομική';
  const carrierBeneficiaries =
    useSelector(state => state.forms.carrier.carrierBeneficiaries) || [];

  // Updates the beneficiaries list in redux
  const setBeneficiaries = next => {
    dispatch(
      updateFormField({
        group: 'carrier',
        key: 'carrierBeneficiaries',
        value: next,
      })
    );
  };

  // Adds one empty row automatically for individual businesses
  const isMounted = useRef(false);
  useEffect(() => {
    // Skip cleanup on initial mount, only run on actual legalType changes
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (isAtomiki && carrierBeneficiaries.length === 0) {
      setBeneficiaries([createEmptyBeneficiary()]);
    } else if (!isAtomiki) {
      setBeneficiaries([]);
    }
  }, [isAtomiki]);

  // On initial load, if Ατομική and no rows exist, add one empty row
  useEffect(() => {
    if (isAtomiki && carrierBeneficiaries.length === 0) {
      setBeneficiaries([createEmptyBeneficiary()]);
    }
  }, []);

  // Keeps only numeric AFM characters and limits length
  const onAfmChange = (rowId, value) => {
    const numericOnly = value.replace(/\D/g, '').slice(0, 9);
    onCellChange(rowId, 'afm', numericOnly);
  };

  // Adds a new empty row
  const addRow = () => {
    const next = [
      ...carrierBeneficiaries,

      {
        id: Date.now().toString(),
        fullName: '',
        afm: '',
        dateOfBirth: '',
        participationRate: '',
        role: '',
      },
    ];
    setBeneficiaries(next);
  };

  // Updates a single field in a specific row
  const onCellChange = (rowId, field, value) => {
    const next = carrierBeneficiaries.map(r =>
      r.id === rowId ? { ...r, [field]: value } : r
    );
    setBeneficiaries(next);
  };

  // Validates and clamps participation so total does not exceed 100
  const onParticipationChange = (rowId, value) => {
    if (value === null || value === undefined || value === '') {
      onCellChange(rowId, 'participationRate', '');
      return;
    }
    const cleaned = String(value).replace(/[^0-9.]/g, '');
    const intVal = Math.floor(Number(cleaned));
    if (!Number.isFinite(intVal)) return;

    const sumWithoutCurrent = carrierBeneficiaries.reduce((sum, r) => {
      if (r.id === rowId) return sum;
      const v = Number(String(r.participationRate).replace(/[^0-9.]/g, ''));
      return sum + (Number.isFinite(v) ? v : 0);
    }, 0);

    const remaining = Math.max(0, MAX_TOTAL - sumWithoutCurrent);
    const clampedInt = Math.min(intVal, remaining);
    onCellChange(rowId, 'participationRate', String(clampedInt));
  };

  // Removes a row
  const deleteRow = rowId => {
    const next = carrierBeneficiaries.filter(r => r.id !== rowId);
    setBeneficiaries(next);
  };

  // Editable table columns for individual businesses
  const editableColumns = useMemo(
    () => [
      {
        title: 'Ονοματεπώνυμο',
        dataIndex: 'fullName',
        key: 'fullName',
        width: 112,
        fixed: 'left',
        render: (_, record) => (
          <Form.Item
            style={{ margin: 0 }}
            validateStatus={!record.fullName?.trim() ? 'error' : ''}
            help={!record.fullName?.trim() ? 'Το πεδίο είναι υποχρεωτικό' : ''}
          >
            <Input
              value={record.fullName}
              placeholder="Εισάγετε ονοματεπώνυμο"
              onChange={e =>
                onCellChange(record.id, 'fullName', e.target.value)
              }
            />
          </Form.Item>
        ),
      },
      {
        title: 'Α.Φ.Μ.',
        dataIndex: 'afm',
        key: 'afm',
        width: 112,
        fixed: 'left',
        render: (_, record) => (
          <Form.Item
            style={{ margin: 0 }}
            validateStatus={!record.afm?.trim() ? 'error' : ''}
            help={!record.afm?.trim() ? 'Το πεδίο είναι υποχρεωτικό' : ''}
          >
            <Input
              value={record.afm}
              placeholder="Εισάγετε Α.Φ.Μ."
              inputMode="numeric"
              maxLength={9}
              onChange={e => onAfmChange(record.id, e.target.value)}
            />
          </Form.Item>
        ),
      },
      {
        title: 'Ημ. Γέννησης',
        dataIndex: 'dateOfBirth',
        key: 'dateOfBirth',
        width: 112,
        fixed: 'left',
        render: (_, record) => (
          <Form.Item
            style={{ margin: 0 }}
            validateStatus={!record.dateOfBirth ? 'error' : ''}
            help={
              !record.dateOfBirth?.trim() ? 'Το πεδίο είναι υποχρεωτικό' : ''
            }
          >
            <div className="beneficial-owners__cell-date">
              <DatePicker
                value={record.dateOfBirth}
                placeholder="Εισάγετε Ημ. Γέννησης"
                allowClear
                onChange={(_, dateString) =>
                  onCellChange(record.id, 'dateOfBirth', dateString)
                }
              />
            </div>
          </Form.Item>
        ),
      },
      {
        title: '% Συμμετοχής',
        dataIndex: 'participationRate',
        key: 'participationRate',
        width: 112,
        align: 'left',
        render: (_, record) => {
          // Treat empty participation as required
          const participationMissing =
            record.participationRate === '' ||
            record.participationRate === null ||
            record.participationRate === undefined;

          // Convert stored value to number for InputNumber
          const numericValue = !participationMissing
            ? Number(String(record.participationRate).replace(/[^0-9.]/g, ''))
            : null;

          return (
            <Form.Item
              style={{ margin: 0 }}
              validateStatus={participationMissing ? 'error' : ''}
              help={participationMissing ? 'Το πεδίο είναι υποχρεωτικό' : ''}
            >
              <InputNumber
                value={Number.isFinite(numericValue) ? numericValue : null}
                placeholder="Εισάγετε % Συμμετοχής"
                min={0}
                max={100}
                step={1}
                precision={0}
                controls={false}
                onChange={val => onParticipationChange(record.id, val)}
              />
            </Form.Item>
          );
        },
      },
      {
        title: 'Ιδιότητα',
        dataIndex: 'role',
        key: 'role',
        width: 112,
        fixed: 'left',
        render: (_, record) => (
          <Form.Item
            style={{ margin: 0 }}
            validateStatus={!record.role?.trim() ? 'error' : ''}
            help={!record.role?.trim() ? 'Το πεδίο είναι υποχρεωτικό' : ''}
          >
            <Input
              value={record.role}
              placeholder="Εισάγετε Ιδιότητα"
              onChange={e => onCellChange(record.id, 'role', e.target.value)}
            />
          </Form.Item>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 40,
        align: 'center',
        render: (_, record) => (
          <div className="deletebtnBin">
            <Bin
              style={{ cursor: 'pointer', width: 20, height: 17 }}
              className="bin-icon"
              onClick={() => deleteRow(record.id)}
            />
          </div>
        ),
      },
    ],
    [carrierBeneficiaries]
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
        }}
      >
        <p className="mt-6 mr-0 color-blue-3 text-bold font-size-24">
          Στοιχεία Πραγματικών Δικαιούχων
        </p>
      </div>

      <div className="custom-table">
        <Table
          className="results-table"
          rowKey="id"
          columns={isAtomiki ? editableColumns : readonlyColumns}
          dataSource={carrierBeneficiaries}
          pagination={false}
        />
      </div>
      {isAtomiki && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: -90,
          }}
        >
          <Button className="addMoreBtn2" onClick={addRow}>
            Προσθήκη
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubTab3;
